import React, { forwardRef, useMemo, useRef, useState } from 'react';
import { cx } from 'linaria';
import { useMediaQuery } from 'fronton-react';
import { mergeRefs } from 'common/utils';
import { AkinatorCompletion, SlicedSuggestions } from 'types/suggestions';
import { suggestionsContainer, suggestionsContainerKz, suggestionsRoot, suggestionsActive } from './styles';
import { Suggestions } from './suggestions';
import { SuggestionPreloader } from './suggestions-preloader';
import { Error } from './suggestions-error';
import { SuggestionsModalContainer } from './suggestions-modal-container';
import { SuggestionContainerRefProvider } from '../hooks';
import { useSuggestionsContainerMaxHeight } from '../hooks/use-suggestions-container-max-height';
import { useLang, useVisualViewportApi } from 'common/hooks';
import { PinnedSuggestion } from 'types/model';
import { useSiteDomain } from '../hooks/use-site-domain';
import { useStoreon } from 'storeon/react';
import { State } from 'store/types';
import { getDomain } from 'utils/get-domain';

type SearchbarModalProps = {
  searchQuery: string;
  updateSearchQuery: (value: string) => void;
  suggestions: SlicedSuggestions | null;
  onMouseEnter: (idx: number) => void;
  onMouseLeave: () => void;
  fetchSuggestions: () => void;
  suggestionsLoading?: boolean;
  onSelectSuggestion?: () => void;
  logoUrl: string;
  searchHref: string;
  error?: boolean;
  expanded: boolean;
  focusGroup: string;
  suggestionSelector: string;
  index: number;
  isSrp: boolean;
  pinnedSuggestions: PinnedSuggestion[];
  completions: AkinatorCompletion[] | null;
  showAkinatorCompletions: boolean;
};

export const SearchbarModal = forwardRef<HTMLDivElement, SearchbarModalProps>(
  (
    {
      searchQuery,
      updateSearchQuery,
      suggestions,
      searchHref,
      onMouseEnter,
      fetchSuggestions,
      onMouseLeave,
      expanded,
      error,
      suggestionSelector,
      index,
      isSrp,
      onSelectSuggestion,
      suggestionsLoading,
      pinnedSuggestions,
      completions,
      showAkinatorCompletions,
    },
    ref,
  ) => {
    const containerRef = useRef<HTMLDivElement | null>(
      null,
    ) as React.MutableRefObject<HTMLDivElement | null>;

    const setContainerRef = useMemo(() => mergeRefs([containerRef, ref]), [ref]);

    const { host } = useStoreon<State>('host');
    const domain = getDomain(host);
    const { isKzDomain } = useSiteDomain(domain);
    const { lang } = useLang();
    const showSuggestions = !error && suggestions;

    const isMobile = useMediaQuery({
      maxWidth: 767,
    });

    const [safeAreaHeight, setSafeAreaHeight] = useState('100vh');

    useVisualViewportApi({ handle: 'resize' }, (visualView) => {
      setSafeAreaHeight(`${visualView.height}px`);
    });

    useSuggestionsContainerMaxHeight(containerRef, !isMobile && expanded);

    const style = isMobile ? { height: safeAreaHeight } : {};

    if (isKzDomain && lang === 'kk') {
      return (
        <div className={cx(suggestionsRoot, expanded && suggestionsActive)} role="dialog">
          <SuggestionsModalContainer
            containerRef={setContainerRef}
            className={suggestionsContainerKz}
            style={style}
          />
        </div>
      );
    }

    return (
      <div className={cx(suggestionsRoot, expanded && suggestionsActive)} role="dialog">
        <SuggestionsModalContainer
          containerRef={setContainerRef}
          className={suggestionsContainer}
          style={style}
        >
          <SuggestionContainerRefProvider containerRef={containerRef}>
            {error && <Error fetchSuggestions={fetchSuggestions} />}
            {suggestionsLoading && <SuggestionPreloader />}
            {showSuggestions && !suggestionsLoading && (
              <Suggestions
                searchQuery={searchQuery}
                updateSearchQuery={updateSearchQuery}
                onSelectSuggestion={onSelectSuggestion}
                isSrp={isSrp}
                suggestions={suggestions}
                suggestionSelector={suggestionSelector}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                searchHref={searchHref}
                index={index}
                pinnedSuggestions={pinnedSuggestions}
                completions={completions}
                showAkinatorCompletions={showAkinatorCompletions}
                isKzDomain={isKzDomain}
                lang={lang}
              />
            )}
          </SuggestionContainerRefProvider>
        </SuggestionsModalContainer>
      </div>
    );
  },
);
